"use client";

import { useCallback, useEffect } from "react";
import { isBridgeMessage } from "./core";
import {
  BRIDGE_VERSION,
  ParentType,
  WSContext,
  WSEventType,
  WSMessage,
} from "./types";

type MessageHandler = (msg: WSMessage, event: MessageEvent) => void;

interface UseParentBridgeOptions {
  // Map appId -> iframe window + origin
  getIframeWindowByAppId: (appId: number) => Window | null;
  getIframeOriginByAppId: (appId: number) => string | null;
  // Parent type (workspace or organization)
  parentType?: ParentType | "workspace" | "organization";
  // Enable debug logging
  debug?: boolean;
  // Callback khi nhận message từ app
  onMessage?: MessageHandler;
}

export function useParentBridge(options: UseParentBridgeOptions) {
  const {
    getIframeWindowByAppId,
    getIframeOriginByAppId,
    parentType = ParentType.WORKSPACE,
    debug = false,
    onMessage,
  } = options;

  // DEBUG: Listen to ALL raw postMessage events when debug=true
  useEffect(() => {
    if (!debug) return;

    console.log(
      "[useParentBridge] 🔍 Debug mode enabled - listening to ALL postMessage events",
    );
    const debugHandler = (event: MessageEvent) => {
      console.log("[useParentBridge] 🔴 Raw postMessage received:", {
        origin: event.origin,
        data: event.data,
      });
    };
    window.addEventListener("message", debugHandler);
    return () => window.removeEventListener("message", debugHandler);
  }, [debug]);

  // Lắng nghe message từ app
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handler = (event: MessageEvent) => {
      const data = event.data;
      if (!isBridgeMessage(data)) return;

      // TODO: check origin whitelist nếu cần
      onMessage?.(data, event);
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [onMessage]);

  // gửi message tới app cụ thể
  const postToApp = useCallback(
    <T>(appId: number, type: WSEventType | string, payload?: T) => {
      const win = getIframeWindowByAppId(appId);
      const origin = getIframeOriginByAppId(appId) ?? "*";
      if (!win) return;

      const msg: WSMessage<T> = {
        namespace: "WS_BRIDGE",
        from: parentType,
        app_id: appId,
        type,
        version: BRIDGE_VERSION,
        payload,
      };

      win.postMessage(msg, origin);
    },
    [getIframeWindowByAppId, getIframeOriginByAppId, parentType],
  );

  // tiện ích: gửi HELLO (WS_HELLO or ORG_HELLO)
  const sendHello = useCallback(
    (appId: number, context: WSContext) => {
      const eventType =
        parentType === ParentType.ORGANIZATION
          ? WSEventType.ORG_HELLO
          : WSEventType.WS_HELLO;
      postToApp<WSContext & { [k: string]: any }>(appId, eventType, context);
    },
    [postToApp, parentType],
  );

  // tiện ích: gửi CONTEXT_UPDATE (WS_CONTEXT_UPDATE or ORG_CONTEXT_UPDATE)
  const sendContextUpdate = useCallback(
    (appId: number, context: WSContext) => {
      const eventType =
        parentType === ParentType.ORGANIZATION
          ? WSEventType.ORG_CONTEXT_UPDATE
          : WSEventType.WS_CONTEXT_UPDATE;
      postToApp<WSContext>(appId, eventType, context);
    },
    [postToApp, parentType],
  );

  // tiện ích: điều hướng app (WS_APP_NAVIGATE or ORG_APP_NAVIGATE)
  const navigateApp = useCallback(
    (appId: number, route: string, params?: Record<string, any>) => {
      const eventType =
        parentType === ParentType.ORGANIZATION
          ? WSEventType.ORG_APP_NAVIGATE
          : WSEventType.WS_APP_NAVIGATE;
      postToApp(appId, eventType, { route, params });
    },
    [postToApp, parentType],
  );

  return {
    postToApp,
    sendHello,
    sendContextUpdate,
    navigateApp,
  };
}

// Backward compatibility aliases
export const useWorkspaceBridge = useParentBridge;
export const useOrganizationBridge = useParentBridge;

// Type aliases for backward compatibility
export type UseWorkspaceBridgeOptions = UseParentBridgeOptions;
export type UseOrganizationBridgeOptions = UseParentBridgeOptions;
export type UseWorkspaceBridgeReturn = ReturnType<typeof useParentBridge>;
export type UseOrganizationBridgeReturn = ReturnType<typeof useParentBridge>;
